import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import {
  ArrowLeft,
  CheckCircle,
  DownloadSimple,
  Export,
  FloppyDisk,
  GridFour,
  HardDrive,
  ImageSquare,
  Plus,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import { AlbumPreview } from "./AlbumPreview";
import { SortablePhoto } from "./SortablePhoto";
import { buildAlbumDocument, type PhotoFolioProject, type PhotoItem, type TemplateId } from "../domain/project";
import { useAssetUrls } from "../hooks/useAssetUrls";
import { useAutosave } from "../hooks/useAutosave";
import { importPhoto } from "../media/process";
import { db } from "../storage/database";
import { deleteAssetFiles } from "../storage/asset-store";
import { readAssetFile } from "../storage/asset-store";
import { createOfflineHtml } from "../exporters/html-exporter";
import { createProjectArchive } from "../exporters/project-exporter";
import { downloadBlob, safeFilename } from "../exporters/download";

interface EditorProps {
  initialProject: PhotoFolioProject;
  onBack: () => void;
  onProjectChange: (project: PhotoFolioProject) => void;
}

const templates: { id: TemplateId; name: string; description: string }[] = [
  { id: "paper", name: "纸上影集", description: "大留白与单幅叙事" },
  { id: "timeline", name: "时间札记", description: "按顺序阅读旅程" },
  { id: "gallery", name: "沉浸画廊", description: "紧凑浏览更多照片" },
];

export function Editor({ initialProject, onBack, onProjectChange }: EditorProps) {
  const [project, setProject] = useState(initialProject);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [importing, setImporting] = useState(false);
  const [storage, setStorage] = useState<{ usage: number; quota: number } | null>(null);
  const [mobileMode, setMobileMode] = useState<"settings" | "organize" | "preview">("organize");
  const [exportOpen, setExportOpen] = useState(false);
  const [estimatedBytes, setEstimatedBytes] = useState<number | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const urls = useAssetUrls(project.id, project.items, "preview");
  const thumbUrls = useAssetUrls(project.id, project.items, "thumbnail");
  const document = useMemo(() => buildAlbumDocument(project), [project]);
  const handleSaved = useCallback((saved: PhotoFolioProject) => {
    setProject((current) => current.updatedAt === saved.updatedAt ? current : saved);
    onProjectChange(saved);
  }, [onProjectChange]);
  const saveState = useAutosave(project, handleSaved);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const patchProject = (patch: Partial<PhotoFolioProject>) => {
    setProject((current) => ({ ...current, ...patch }));
  };

  const updateItem = (id: string, patch: Partial<PhotoItem>) => {
    setProject((current) => ({
      ...current,
      items: current.items.map((item) => item.id === id ? { ...item, ...patch } : item),
    }));
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    setProject((current) => {
      const from = current.items.findIndex((item) => item.id === active.id);
      const to = current.items.findIndex((item) => item.id === over.id);
      return { ...current, items: arrayMove(current.items, from, to).map((item, order) => ({ ...item, order })) };
    });
  };

  const handleFiles = async (files: FileList | File[]) => {
    const selected = Array.from(files);
    if (!selected.length) return;
    setImporting(true);
    setError("");
    const imported: PhotoItem[] = [];
    const failures: string[] = [];
    for (const file of selected) {
      try {
        const result = await importPhoto(project.id, file, project.items.length + imported.length);
        imported.push(result.item);
      } catch (reason) {
        failures.push(reason instanceof Error ? reason.message : `${file.name} 导入失败`);
      }
    }
    if (imported.length) {
      setProject((current) => ({
        ...current,
        items: [...current.items, ...imported],
        coverAssetId: current.coverAssetId || imported[0].assetId,
      }));
      setNotice(`已导入 ${imported.length} 张照片`);
    }
    if (failures.length) setError(failures.join("；"));
    setImporting(false);
    const estimate = await navigator.storage?.estimate?.();
    if (estimate?.usage && estimate?.quota) setStorage({ usage: estimate.usage, quota: estimate.quota });
  };

  const removeItem = async (item: PhotoItem) => {
    await Promise.all([deleteAssetFiles(project.id, item.assetId), db.assets.delete(item.assetId)]);
    setProject((current) => {
      const items = current.items.filter((entry) => entry.id !== item.id).map((entry, order) => ({ ...entry, order }));
      return { ...current, items, coverAssetId: current.coverAssetId === item.assetId ? items[0]?.assetId : current.coverAssetId };
    });
  };

  const exportHtml = async () => {
    if (!project.items.length) return setError("请先导入至少一张照片");
    setNotice("正在生成离线网页");
    try {
      const html = await createOfflineHtml(project);
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      downloadBlob(blob, `${safeFilename(project.title)}.html`);
      setNotice(`离线网页已生成，约 ${(blob.size / 1024 / 1024).toFixed(1)} MB`);
      setExportOpen(false);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "导出失败");
    }
  };

  const calculateEstimate = async (quality = project.exportQuality) => {
    setEstimatedBytes(null);
    if (!project.items.length) return;
    try {
      const blobs = await Promise.all(project.items.map((item) => readAssetFile(
        project.id,
        item.assetId,
        quality === "archive" ? "original" : "preview",
      )));
      const raw = blobs.reduce((sum, blob) => sum + blob.size, 0);
      setEstimatedBytes(Math.round(raw * (quality === "light" ? .75 : 1.34) + 180_000));
    } catch {
      setEstimatedBytes(null);
    }
  };

  const openExportCenter = async () => {
    setExportOpen(true);
    await calculateEstimate();
  };

  const exportDraft = async () => {
    if (!project.items.length) return setError("空项目不需要备份");
    setNotice("正在整理项目草稿");
    try {
      const archive = await createProjectArchive(project);
      downloadBlob(archive, `${safeFilename(project.title)}.photofolio`);
      setNotice("项目草稿已下载");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "草稿导出失败");
    }
  };

  return (
    <main className={`editor-shell mobile-${mobileMode}`}>
      <header className="editor-topbar">
        <button className="topbar-back" type="button" onClick={onBack}><ArrowLeft size={19} />所有影集</button>
        <div className="topbar-title"><strong>{project.title}</strong><span className={`save-state ${saveState}`}>
          {saveState === "saved" ? <CheckCircle size={15} /> : saveState === "saving" ? <FloppyDisk size={15} /> : <WarningCircle size={15} />}
          {saveState === "saved" ? "已保存到此浏览器" : saveState === "saving" ? "正在保存" : "保存失败"}
        </span></div>
        <div className="topbar-actions">
          <button className="secondary-button compact" type="button" onClick={exportDraft}><DownloadSimple size={18} />备份草稿</button>
          <button className="primary-button compact" type="button" onClick={openExportCenter}><Export size={18} />导出 HTML</button>
        </div>
      </header>

      {(notice || error) ? <div className={error ? "notice error" : "notice"} role="status">
        {error || notice}<button type="button" onClick={() => { setNotice(""); setError(""); }}>关闭</button>
      </div> : null}

      <div className="editor-grid">
        <aside className="settings-panel">
          <section>
            <h2>影集信息</h2>
            <label><span>标题</span><input value={project.title} onChange={(event) => patchProject({ title: event.target.value })} /></label>
            <label><span>副标题</span><textarea rows={3} value={project.subtitle} onChange={(event) => patchProject({ subtitle: event.target.value })} /></label>
            <label><span>制作人</span><input value={project.author} placeholder="可选" onChange={(event) => patchProject({ author: event.target.value })} /></label>
            <div className="field-pair">
              <label><span>日期</span><input type="date" value={project.date} onChange={(event) => patchProject({ date: event.target.value })} /></label>
              <label><span>地点</span><input value={project.location} placeholder="例如：北海道" onChange={(event) => patchProject({ location: event.target.value })} /></label>
            </div>
            <label><span>同行人物</span><input value={project.people.join("，")} placeholder="用逗号分隔姓名" onChange={(event) => patchProject({ people: event.target.value.split(/[，,]/).map((item) => item.trim()).filter(Boolean) })} /></label>
          </section>
          <section>
            <h2>阅读方式</h2>
            <div className="template-list">
              {templates.map((template) => <button key={template.id} type="button" className={project.template === template.id ? "template-option active" : "template-option"} onClick={() => patchProject({ template: template.id })}>
                <span className="template-icon">{template.id === "gallery" ? <GridFour size={20} /> : template.id === "timeline" ? <HardDrive size={20} /> : <ImageSquare size={20} />}</span>
                <span><strong>{template.name}</strong><small>{template.description}</small></span>
              </button>)}
            </div>
          </section>
          <section className="storage-info">
            <h2>本地存储</h2>
            <p>照片不会上传。清除浏览器数据会删除项目，请定期下载草稿。</p>
            {storage ? <small>已使用约 {(storage.usage / 1024 / 1024).toFixed(0)} MB，可用配额约 {(storage.quota / 1024 / 1024 / 1024).toFixed(1)} GB</small> : null}
          </section>
        </aside>

        <section className="photo-editor">
          <div className="photo-editor-head">
            <div><h2>照片与文字</h2><p>{project.items.length ? `${project.items.length} 张照片，拖动左侧手柄调整顺序。` : "先导入照片，再从一张照片开始讲述。"}</p></div>
            <button className="secondary-button compact" type="button" disabled={importing} onClick={() => fileInput.current?.click()}>
              <Plus size={17} />{importing ? "正在处理" : "导入照片"}
            </button>
            <input ref={fileInput} type="file" multiple accept="image/jpeg,image/png,image/webp" hidden onChange={(event) => event.target.files && handleFiles(event.target.files)} />
          </div>
          {project.items.length ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={project.items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
                <div className="photo-list">
                  {project.items.map((item, index) => <SortablePhoto key={item.id} item={item} index={index} url={thumbUrls[item.assetId]} isCover={project.coverAssetId === item.assetId} onChange={(patch) => updateItem(item.id, patch)} onCover={() => patchProject({ coverAssetId: item.assetId })} onRemove={() => removeItem(item)} />)}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <button className="drop-empty" type="button" onClick={() => fileInput.current?.click()} onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); handleFiles(event.dataTransfer.files); }}>
              <ImageSquare size={38} /><strong>把照片放到这里</strong><span>支持 JPEG、PNG、WebP，可一次选择多张。</span>
            </button>
          )}
        </section>

        <aside className="preview-panel">
          <div className="preview-head"><span>实时预览</span><span>{templates.find((item) => item.id === project.template)?.name}</span></div>
          <div className="preview-scroll"><AlbumPreview document={document} urls={urls} /></div>
        </aside>
      </div>
      <nav className="mobile-editor-nav" aria-label="编辑器模式">
        <button type="button" className={mobileMode === "settings" ? "active" : ""} onClick={() => setMobileMode("settings")}>设置</button>
        <button type="button" className={mobileMode === "organize" ? "active" : ""} onClick={() => setMobileMode("organize")}>整理</button>
        <button type="button" className={mobileMode === "preview" ? "active" : ""} onClick={() => setMobileMode("preview")}>预览</button>
      </nav>
      {exportOpen ? <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setExportOpen(false)}>
        <section className="export-dialog" role="dialog" aria-modal="true" aria-labelledby="export-title">
          <div className="dialog-head"><div><span>生成与保存</span><h2 id="export-title">导出独立 HTML</h2></div><button className="icon-button" type="button" aria-label="关闭" onClick={() => setExportOpen(false)}><X size={20} /></button></div>
          <div className="export-summary"><div><span>项目</span><strong>{project.title}</strong></div><div><span>照片</span><strong>{project.items.length} 张</strong></div><div><span>模板</span><strong>{templates.find((item) => item.id === project.template)?.name}</strong></div><div><span>预计大小</span><strong>{estimatedBytes ? `${(estimatedBytes / 1024 / 1024).toFixed(1)} MB` : "正在计算"}</strong></div></div>
          <fieldset className="quality-options"><legend>图片质量</legend>
            {([
              ["light", "轻量", "最长边约 1600px，适合发送"],
              ["standard", "标准", "平衡画质与体积，推荐"],
              ["archive", "珍藏", "嵌入原图，文件可能很大"],
            ] as const).map(([id, name, description]) => <label key={id} className={project.exportQuality === id ? "quality-option active" : "quality-option"}><input type="radio" name="quality" checked={project.exportQuality === id} onChange={() => { patchProject({ exportQuality: id }); calculateEstimate(id); }} /><span><strong>{name}</strong><small>{description}</small></span></label>)}
          </fieldset>
          <p className="privacy-note">成品不请求网络、不含跟踪代码。即使 PhotoFolio 不再运行，基础照片与文字仍可阅读。</p>
          {estimatedBytes && estimatedBytes > 150 * 1024 * 1024 ? <p className="size-warning">文件超过 150 MB，建议改用标准质量或下载项目草稿长期保存。</p> : null}
          <div className="dialog-actions"><button className="secondary-button" type="button" onClick={() => setExportOpen(false)}>返回编辑</button><button className="primary-button" type="button" onClick={exportHtml}><Export size={18} />生成文件</button></div>
        </section>
      </div> : null}
    </main>
  );
}
